import { MaterialType } from "../../types";
import { isBiddingWindowValid, toApiCalendarDate } from "../../utils";

/**
 * The consultation (Q&A) window and the quotation window can't overlap: the
 * quotation must start strictly after the consultation closes, or the backend
 * rejects the launch.
 */
export const hasConsultationViolation = (generalBidData: any): boolean =>
  !!generalBidData?.consultationEndValue &&
  !!generalBidData?.startDateValue &&
  new Date(generalBidData.startDateValue) <=
    new Date(generalBidData.consultationEndValue);

/**
 * Gate for the "Datos Generales" step of the quotation launch flow.
 *
 * It deliberately does NOT look at per-line supply dates: the per-item
 * delivery date picker was dropped from the joint steps, so joint lines never
 * carry them and requiring them left the "Siguiente" button disabled forever
 * with no visible reason. `resolveSupplyDates` fills them in at submit time.
 */
export const isGeneralDataStepComplete = ({
  generalBidData,
  ordersToSubmit,
}: {
  generalBidData: any;
  ordersToSubmit: any[];
}): boolean => {
  const isService = ordersToSubmit?.some(
    (el: any) => el.type === MaterialType.SERVICES
  );
  // Joint quotations name the whole tender; simple/multiple ones name each line.
  const everyLineNamed = ordersToSubmit?.every((el: any) => el.name);

  return Boolean(
    generalBidData.paymentMethod &&
      isBiddingWindowValid(generalBidData) &&
      generalBidData.paymentTerm &&
      generalBidData.incoTerm &&
      (isService || generalBidData.currency) &&
      (generalBidData.name || everyLineNamed) &&
      !hasConsultationViolation(generalBidData)
  );
};

const toIsoOrUndefined = (value: any): string | undefined =>
  value ? toApiCalendarDate(value) : undefined;

/**
 * Supply dates for a line at submit time: its own dates when the flow captured
 * them (simple/multiple quotations still show the picker), otherwise the
 * bidding window. Mirrors the fallback already used by OrderByRequest.
 */
export const resolveSupplyDates = (mat: any, generalBidData: any) => ({
  startSupplyDate: toIsoOrUndefined(
    mat?.startSupplyDate || generalBidData?.startDateValue
  ),
  endSupplyDate: toIsoOrUndefined(
    mat?.endSupplyDate || generalBidData?.endDateValue
  ),
});
